import { type ReactNode } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import stoneStepsImage from "@/assets/masonry-brick-steps-north-haledon-nj.webp";
import concreteDrivewayImage from "@/assets/concrete-driveway-homepage.webp";
import paverWalkwayStepsNorthCaldwell from "@/assets/walkways/paver-walkway-steps-installation-north-caldwell-nj-2.webp";
import servicesHeroImage from "@/assets/stone-retaining-wall-steps-franklin-lakes-nj-2.webp";
import epHenryRetainingWallFranklinLakes from "@/assets/ep-henry-stone-retaining-wall-franklin-lakes-nj.webp";
import stoneMasonryOakland from "@/assets/stone-masonry-installation-oakland-nj.webp";
import waterproofingBasementRidgewood from "@/assets/waterproofing-basement-repair-ridgewood-nj-t.webp";

const bodyLinkClass = "text-secondary underline underline-offset-4 hover:text-secondary/80 font-medium";

interface ServiceData {
  label: string;
  title: string;
  content: ReactNode;
  link: string;
  linkText: string;
  image: string;
  alt: string;
}

const masonryServices: ServiceData[] = [
  {
    label: "Masonry",
    title: "Retaining Wall Installation",
    link: "/masonry-services/retaining-walls",
    linkText: "Explore Retaining Walls",
    alt: "EP Henry stone retaining wall installation in Franklin Lakes, NJ by Masonry Pro Construction",
    image: epHenryRetainingWallFranklinLakes,
    content: <> North Jersey yards slope, and the glacial soil around Totowa holds water, so a wall needs real drainage behind it or it starts to lean. We handle <Link to="/masonry-services/retaining-walls" className={bodyLinkClass}>retaining wall installation in Totowa</Link> and across the four counties, from garden walls to engineered walls over four feet. If yours is already cracking or bulging, we'll tell you straight whether <Link to="/masonry-services/retaining-wall-repair" className={bodyLinkClass}>retaining wall repair</Link> can save it. </>,
  },
  {
    label: "Masonry",
    title: "Stone Masonry Installation",
    link: "/masonry-services/stone-masonry",
    linkText: "Explore Stone Masonry",
    alt: "Stone masonry installation on a residential property in Oakland, NJ by Masonry Pro Construction",
    image: stoneMasonryOakland,
    content: <> Natural stone gives a home weight and character that other materials just can't match. We do <Link to="/masonry-services/stone-masonry" className={bodyLinkClass}>stone masonry installation</Link> for walls, columns and facades, and <Link to="/masonry-services/stone-veneer" className={bodyLinkClass}>stone veneer installation</Link> when you want the look of full stone without the weight. Older stonework gets <Link to="/masonry-services/stone-masonry-repair" className={bodyLinkClass}>stone masonry repair</Link>, which means repointing worn joints and resetting loose stones before freeze-thaw opens them wider. </>,
  },
  {
    label: "Masonry",
    title: "Steps & Stoops Installation",
    link: "/masonry-services/steps-stoops",
    linkText: "Explore Steps & Stoops",
    alt: "Newly installed masonry brick steps leading to a residence in North Haledon, Passaic County, NJ by Masonry Pro Construction",
    image: stoneStepsImage,
    content: <> Front steps take daily foot traffic, then snow, ice and salt all winter. We build <Link to="/masonry-services/steps-stoops" className={bodyLinkClass}>steps and stoops installation in Totowa</Link> on proper footings, so they don't crack or pull away from the house a few years later. When treads crack or stones come loose, our <Link to="/masonry-services/steps-stoops-repair" className={bodyLinkClass}>steps and stoops repair</Link> work fixes it, and we'll give you a straight answer on repair versus rebuild. </>,
  },
];

const categoryServices: ServiceData[] = [
  {
    label: "Service Category",
    title: "Paving Contractor",
    link: "/paving-services",
    linkText: "Explore Paving",
    alt: "Newly installed paver walkway and steps leading to a brick home in North Caldwell, Essex County, NJ by Masonry Pro Construction",
    image: paverWalkwayStepsNorthCaldwell,
    content: <> A patio or driveway lasts as long as the base under it, so that's where we spend the time: digging out, compacting stone and setting drainage before a single paver goes down. We handle <Link to="/paving-services/paver-patios" className={bodyLinkClass}>paver patio installation in Totowa</Link> and <Link to="/paving-services/paver-driveways" className={bodyLinkClass}>paver driveway installation</Link> built to stand up to plows and salt. See everything we do as a <Link to="/paving-services" className={bodyLinkClass}>paving contractor in North Jersey</Link>. </>,
  },
  {
    label: "Service Category",
    title: "Concrete Contractor",
    link: "/concrete-services",
    linkText: "Explore Concrete",
    alt: "Concrete driveway installation in North Jersey by Masonry Pro Construction",
    image: concreteDrivewayImage,
    content: <> Additions, garages and decks all start with what's underneath, so we pour footings and foundations level and to code. As a <Link to="/concrete-services" className={bodyLinkClass}>concrete contractor in Totowa</Link>, we also pour driveways, sidewalks and patios, and we fix cracked or sinking slabs before winter makes them worse. </>,
  },
  {
    label: "Service Category",
    title: "Waterproofing Service",
    link: "/waterproofing-services",
    linkText: "Explore Waterproofing",
    alt: "Exterior basement waterproofing in Ridgewood, New Jersey, by Masonry Pro Construction",
    image: waterproofingBasementRidgewood,
    content: <> Water in the basement after every storm won't fix itself. We find where it's getting in and stop it, from inside or outside the foundation, and we seal patios so salt and freeze-thaw don't break them down. See how our <Link to="/waterproofing-services" className={bodyLinkClass}>waterproofing service in North Jersey</Link> works. </>,
  },
];

function HomepageServiceCard({ service }: { service: ServiceData }) {
  return (
    <div className="bg-card rounded-xl border border-border overflow-hidden flex flex-col">
      <img src={service.image} alt={service.alt} className="w-full h-60 object-cover" loading="lazy" width={800} height={600} />
      <div className="p-6 flex flex-col flex-1">
        <span className="text-sm uppercase tracking-wider text-secondary font-semibold mb-2">{service.label}</span>
        <h3 className="text-2xl font-bold mb-3">{service.title}</h3>
        <p className="text-muted-foreground leading-relaxed mb-6">{service.content}</p>
        <Button variant="goldOutline" size="default" asChild className="mt-auto">
          <Link to={service.link}>{service.linkText} <ArrowRight className="h-4 w-4" /></Link>
        </Button>
      </div>
    </div>
  );
}

export function ServicesSection() {
  return (
    <section id="services">
      {/* Section Header with Background Image */}
      <div className="relative py-16">
        {/* Background image */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: `url(${servicesHeroImage})` }}
        >
          <div className="absolute inset-0 bg-background/70 backdrop-blur-[2px]" />
        </div>
        
        {/* Content */}
        <div className="container mx-auto px-4 relative z-10">
          <div className="text-center mb-8">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4">
              Expert Masonry, Paving, Concrete, &amp; <span className="text-gradient-gold">Waterproofing Services</span>
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              From paver patios to retaining walls, we build outdoor spaces that last.
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 pt-12">
        <h3 className="text-3xl md:text-4xl font-bold mb-4">
          <span className="text-gradient-gold">Masonry Services in North Jersey</span>
        </h3>
        <p className="text-lg text-muted-foreground leading-relaxed mb-8">
          Masonry is the core of what we do, and it's been the family trade for 35+ years. Beyond the three below, we take on <Link to="/masonry-services/custom-masonry" className={bodyLinkClass}>custom masonry</Link> like driveway columns and garden walls, backyard <Link to="/masonry-services/outdoor-kitchens" className={bodyLinkClass}>outdoor kitchen installation</Link>, <Link to="/masonry-services/fire-pits" className={bodyLinkClass}>fire pit installation</Link> and <Link to="/masonry-services/fireplace-installation" className={bodyLinkClass}>fireplace installation</Link>, plus traditional <Link to="/masonry-services/brick-work" className={bodyLinkClass}>brick work</Link>, structural <Link to="/masonry-services/block-work" className={bodyLinkClass}>block work</Link>, and <Link to="/masonry-services/commercial-masonry" className={bodyLinkClass}>commercial masonry and concrete</Link> across North Jersey.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
          {masonryServices.map((service) => <HomepageServiceCard key={service.title} service={service} />)}
        </div>
        <h3 className="text-3xl md:text-4xl font-bold mb-8">
          <span className="text-gradient-gold">Paving, Concrete, and Waterproofing</span>
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {categoryServices.map((service) => <HomepageServiceCard key={service.title} service={service} />)}
        </div>
      </div>

      {/* View All Services Button */}
      <div className="py-12 bg-background">
        <div className="container mx-auto px-4">
          <div className="text-center">
            <Button variant="goldOutline" size="lg" asChild>
              <Link to="/services" className="flex items-center gap-2">
                View All Services <ArrowRight className="h-5 w-5" />
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
}
